import {isFeatureEnabled} from 'feature-flags';
import {Action, Card, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {assoc} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useDeleteEarningMutation} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  ApplyVehiclePresetForm,
  CreateVehicleEarningTransactionPreset,
  CreateVehiclePresetForm,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  usePermissions,
  VehicleAddEarningForm,
  VehicleEarningCostForm,
  VehicleEditEarningForm,
} from '@dms/shared';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {EditEarningForm} from './EditEarningForm';

interface VehicleEarningsGridCardProps extends TestIdProps {
  vehicleId: string;
  isCostsEditable?: boolean;
}

const PREDEFINED_COLUMNS = ['name'] as const;

export function VehicleEarningsGridCard(props: VehicleEarningsGridCardProps) {
  const [deleteEarning] = useDeleteEarningMutation();
  const refreshDataGrid = () => refreshDmsDatagrid('vehicle_earnings');

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [canAddEarning] = usePermissions({
    permissionKeys: ['vehicleAddEarning'],
    scopes: {
      vehicleAddEarning: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  /**
   * Before memo was used because of a bug in DataGrid. See https://carvago.atlassian.net/browse/T20-18662. Now compiler is used to avoid unnecessary re-renders.
   */

  const queryModifier = assoc('vehicle', props.vehicleId);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const earningId = isArray(rowId) ? rowId[0] : rowId;

    switch (actionKey) {
      case 'delete':
        openDeleteDialog({
          name: getColumnData<string>('name')?.value,
          'data-testid': suffixTestId('deleteDialog', props),
          onConfirm: () =>
            deleteEarning({vehicleId: props.vehicleId, earningId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
        break;

      case 'edit':
        if (isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)) {
          openDialog(
            <VehicleEditEarningForm
              data-testid={suffixTestId('editDialog', props)}
              earningId={earningId}
              vehicleId={props.vehicleId}
              onDiscard={closeCurrentDialog}
              onSuccess={() => {
                refreshData();
                closeCurrentDialog();
              }}
            />,
            {title: i18n.t('entity.vehicle.earnings.editEarning')}
          );
        } else {
          openDialog(
            <EditEarningForm
              data-testid={suffixTestId('editDialog', props)}
              earningId={earningId}
              vehicleId={props.vehicleId}
              onCancel={closeCurrentDialog}
              onSuccess={() => {
                refreshData();
                closeCurrentDialog();
              }}
              isCostsEditable={props.isCostsEditable}
            />,
            {title: i18n.t('entity.vehicle.earnings.editEarning')}
          );
        }
        break;
    }
  };

  const openAddDialog = () =>
    isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)
      ? openDialog(
          <VehicleAddEarningForm
            data-testid={suffixTestId('addDialog', props)}
            vehicleId={props.vehicleId}
            onDiscard={closeCurrentDialog}
            onSuccess={() => {
              refreshDataGrid();
              closeCurrentDialog();
            }}
          />,
          {title: i18n.t('entity.vehicle.earnings.addEarning')}
        )
      : openDialog(
          <VehicleEarningCostForm
            data-testid={suffixTestId('addDialog', props)}
            entity="earning"
            vehicleId={props.vehicleId}
            onCancel={closeCurrentDialog}
            onSuccess={() => {
              refreshDataGrid();
              closeCurrentDialog();
            }}
            isCostsEditable={props.isCostsEditable}
          />,
          {title: i18n.t('entity.vehicle.earnings.addEarning')}
        );

  const openApplyPresetDialog = () =>
    openDialog(
      <ApplyVehiclePresetForm
        entity="earning"
        vehicleId={props.vehicleId}
        onCancel={closeCurrentDialog}
        onSuccess={() => {
          refreshDataGrid();
          closeCurrentDialog();
        }}
        data-testid={suffixTestId('applyPresetDialog', props)}
      />,

      {title: i18n.t('entity.earningsCosts.actions.addPreset'), size: 'small'}
    );

  const openCreatePresetDialog = () =>
    isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)
      ? openDialog(
          <CreateVehicleEarningTransactionPreset
            vehicleId={props.vehicleId}
            onDiscard={closeCurrentDialog}
            onSuccess={closeCurrentDialog}
            data-testid={suffixTestId('createPresetDialog', props)}
          />,
          {title: i18n.t('entity.earningsCosts.actions.savePreset'), size: 'small'}
        )
      : openDialog(
          <CreateVehiclePresetForm
            entity="earning"
            vehicleId={props.vehicleId}
            onCancel={closeCurrentDialog}
            onSuccess={closeCurrentDialog}
            data-testid={suffixTestId('createPresetDialog', props)}
          />,

          {title: i18n.t('entity.earningsCosts.actions.savePreset'), size: 'small'}
        );

  const actions = buildArray<Action>()
    .when(canAddEarning, {
      type: 'button',
      variant: 'link',
      onClick: openCreatePresetDialog,
      leftIcon: 'content/save',
      title: i18n.t('entity.earningsCosts.actions.savePreset'),
      'data-testid': suffixTestId('createPreset', props),
    })
    .when(canAddEarning, {
      type: 'button',
      variant: 'link',
      onClick: openApplyPresetDialog,
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.earningsCosts.actions.addFromPreset'),
      'data-testid': suffixTestId('addPresetButton', props),
    })
    .when(canAddEarning, {
      type: 'button',
      variant: 'link',
      onClick: openAddDialog,
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.earningsCosts.actions.addItem'),
      'data-testid': suffixTestId('addButton', props),
    });

  return (
    <>
      <Card title={i18n.t('entity.earningsCosts.labels.earnings')} actions={actions}>
        <Box height={65}>
          <DmsDataGrid
            gridCode="vehicle_earnings"
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            emptyState={{
              action: canAddEarning
                ? {
                    title: i18n.t('entity.earningsCosts.actions.addEarning'),
                    leftIcon: 'content/add_circle',
                    onClick: openAddDialog,
                  }
                : undefined,
            }}
            data-testid={suffixTestId('grid', props)}
          />
        </Box>
      </Card>
    </>
  );
}
